import { useRef } from "react";
import { Link } from "react-router-dom";
import {
    motion,
    useScroll,
    useTransform,
    useSpring,
    useInView,
} from "framer-motion";
import { ArrowUpRight, Phone, MapPin, Clock, Quote } from "lucide-react";
import MaskedReveal from "@/components/MaskedReveal";
import Marquee from "@/components/Marquee";
import GoogleRatingBadge from "@/components/GoogleRatingBadge";
import Seo from "@/components/Seo";
import AnswerBlock from "@/components/AnswerBlock";
import { fadeUp, stagger, scaleIn } from "@/lib/motion";
import { TID } from "@/lib/testIds";
import { getPageSeo } from "@/lib/seo";
import {
    IMAGES,
    PRESTATIONS,
    PROJECTIONS,
    SESSION_STEPS,
    TIMELINE,
    FAQ_HOME,
    CONTACT,
    REVIEWS,
    GOOGLE_RATING,
} from "@/lib/data";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";

/* ---------------- Hero ---------------- */
function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const yAlps = useTransform(scrollYProgress, [0, 1], ["0%", "45%"]);

    return (
        <section
            ref={ref}
            className="relative min-h-[100svh] flex flex-col pt-28 sm:pt-32 lg:pt-20 pb-10 lg:pb-12 overflow-x-clip"
        >
            {/* Texture CSS only — jamais d'image Alpes ici (volait le LCP) */}
            <motion.div
                style={{ y: yAlps }}
                className="absolute inset-x-0 top-0 h-[70vh] pointer-events-none opacity-40"
                aria-hidden="true"
            >
                <div className="w-full h-full bg-[radial-gradient(ellipse_at_30%_20%,hsl(var(--sage)/0.35),transparent_55%),linear-gradient(180deg,hsl(var(--terracotta)/0.12),transparent_50%,hsl(var(--creme)))]" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-creme/40 to-creme" />
            </motion.div>

            <div className="relative flex-1 min-w-0 w-full max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-8 xl:gap-10 content-center lg:items-center py-4 lg:py-0">
                {/* Left — headline + CTAs (above the fold on mobile) */}
                <div className="col-span-full lg:col-span-7 relative flex flex-col min-w-0 max-w-full">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.1 }}
                        className="flex items-center gap-3 mb-5 lg:mb-6 min-w-0"
                    >
                        <span className="w-8 sm:w-10 h-px bg-brown shrink-0" />
                        <GoogleRatingBadge compact className="min-w-0" />
                    </motion.div>

                    <MaskedReveal
                        as="h1"
                        className="font-serif font-light text-[clamp(2.15rem,8.2vw,3.75rem)] md:text-[clamp(3.25rem,7vw,4.5rem)] lg:text-[clamp(3.75rem,5.2vw,6.25rem)] leading-[0.94] text-brown tracking-tight max-w-full"
                        lines={[
                            "Réflexologue",
                            <em key="e" className="italic text-terracotta-deep">
                                à Challes-les-Eaux.
                            </em>,
                        ]}
                    />

                    <motion.p
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.05, duration: 0.8 }}
                        className="mt-4 lg:mt-5 font-serif italic text-[clamp(1.15rem,2.8vw,1.75rem)] text-terracotta-deep/90 leading-snug max-w-lg"
                    >
                        Écouter le corps, retrouver son équilibre.
                    </motion.p>

                    <AnswerBlock className="mt-5 lg:mt-6">
                        Joëlle Berthaud, réflexologue certifiée à Challes-les-Eaux
                        (Savoie). Accompagnement personnalisé en réflexologie
                        plantaire, palmaire, faciale Dien Chan et Chan&apos;Beauté.
                        Sur rendez-vous au cabinet, 1981 Route Royale.
                    </AnswerBlock>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.4, duration: 0.9 }}
                        className="mt-6 lg:mt-7 flex flex-wrap items-center gap-3 sm:gap-4"
                    >
                        <Link
                            to="/contact"
                            data-testid={TID.hero.book}
                            className="group inline-flex items-center gap-2 sm:gap-3 bg-brown text-creme rounded-full pl-5 sm:pl-6 pr-2 py-2 text-sm transition-colors hover:bg-terracotta-deep max-w-full"
                        >
                            Prendre rendez-vous
                            <span className="w-9 h-9 shrink-0 rounded-full bg-creme text-brown grid place-items-center transition-transform duration-500 group-hover:rotate-45">
                                <ArrowUpRight className="w-4 h-4" />
                            </span>
                        </Link>
                        <Link
                            to="/a-propos"
                            data-testid={TID.hero.discover}
                            className="text-sm text-brown hover:text-terracotta-deep underline underline-offset-4 decoration-brown/30 hover:decoration-terracotta"
                        >
                            Découvrir mon approche →
                        </Link>
                    </motion.div>

                    {/* Reassurance chips — below fold on phone, in-viewport on desktop */}
                    <motion.ul
                        variants={stagger}
                        initial="hidden"
                        animate="show"
                        className="mt-10 lg:mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-xl w-full"
                    >
                        {[
                            { t: "Cabinet", s: "Challes-les-Eaux, Savoie" },
                            { t: "Approche", s: "Personnalisée & douce" },
                            { t: "Méthodes", s: "4 pratiques complémentaires" },
                        ].map((it) => (
                            <motion.li
                                key={it.t}
                                variants={fadeUp}
                                custom={2}
                                className="border-l border-brown/20 pl-4 min-w-0"
                            >
                                <div className="text-[10px] uppercase tracking-[0.24em] text-brown-soft">
                                    {it.t}
                                </div>
                                <div className="text-sm text-brown mt-1">
                                    {it.s}
                                </div>
                            </motion.li>
                        ))}
                    </motion.ul>
                </div>

                {/* Right — portrait + depuis 2018 (below fold on phone) */}
                <div className="col-span-full lg:col-span-5 relative mt-2 lg:mt-0 pb-8 lg:pb-10 min-w-0 max-w-full px-3 sm:px-4 lg:px-0">
                    <motion.div
                        style={{ y: yImg }}
                        initial={{ opacity: 0, scale: 1.1 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="relative aspect-[3/4] w-full max-w-[min(100%,420px)] mx-auto lg:mx-0 lg:max-w-none lg:aspect-auto lg:h-[min(62svh,560px)] xl:h-[min(66svh,620px)]"
                    >
                        <div className="absolute inset-0 overflow-hidden rounded-t-[999px]">
                            <img
                                src="/images/joelle-portrait-640.webp"
                                srcSet={IMAGES.portraitSrcSet}
                                sizes="(max-width: 640px) 85vw, (max-width: 1024px) 420px, 560px"
                                alt="Portrait de Joëlle Berthaud, réflexologue"
                                className="w-full h-full object-cover"
                                width={640}
                                height={644}
                                fetchPriority="high"
                                decoding="async"
                            />
                            <div className="absolute inset-0 bg-terracotta/5 mix-blend-multiply" />
                        </div>

                        {/* Floating tag — offsets left/bottom (pas de translate : Motion les écrase) */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 1.6, duration: 0.7 }}
                            className="absolute z-10 -left-3 sm:-left-5 lg:-left-8 -bottom-5 sm:-bottom-6 lg:-bottom-7 bg-off-white border border-line rounded-md px-4 py-3 shadow-sm max-w-[180px] sm:max-w-[220px]"
                        >
                            <div className="text-[10px] uppercase tracking-widest text-brown-soft">
                                Depuis 2018
                            </div>
                            <div className="font-serif text-base sm:text-lg text-brown mt-1 leading-tight">
                                Formée FFPER, Dien&nbsp;Chan & Chan'Beauté
                            </div>
                        </motion.div>

                        {/* Number */}
                        <div className="absolute -top-4 lg:-top-6 right-0 lg:-right-2 font-serif italic text-[5.5rem] sm:text-[7rem] lg:text-[10rem] leading-none text-terracotta/25 select-none pointer-events-none">
                            *
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Scroll cue */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.8 }}
                className="hidden lg:flex absolute bottom-5 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-brown-soft/90"
            >
                <span className="text-[10px] uppercase tracking-[0.3em]">
                    Défiler
                </span>
                <motion.span
                    animate={{ y: [0, 8, 0] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                    className="w-px h-10 bg-brown-soft/40"
                />
            </motion.div>
        </section>
    );
}

/* ---------------- Projections · Bento des situations ---------------- */
function Projections() {
    const items = [
        {
            span: "col-span-12 md:col-span-6 lg:col-span-6 md:row-span-2",
            variant: "image-dark",
            n: "01",
            t: "Stress & tensions",
            d: "Nuque, épaules, mâchoire — le corps garde en mémoire les journées trop chargées.",
            img: IMAGES.hands_massage,
            imgSrcSet: IMAGES.handsMassageSrcSet,
            imgSizes: "(max-width: 768px) 100vw, 50vw",
        },
        {
            span: "col-span-12 min-[505px]:col-span-6 lg:col-span-3",
            variant: "tint-terracotta",
            n: "02",
            t: "Fatigue",
            d: "Physique, mentale, ou les deux à la fois.",
        },
        {
            span: "col-span-12 min-[505px]:col-span-6 lg:col-span-3",
            variant: "dark",
            n: "03",
            t: "Lâcher-prise",
            d: "S'autoriser à ne rien faire, un moment, vraiment.",
        },
        {
            span: "col-span-12 lg:col-span-6",
            variant: "tint-sage-image",
            n: "04",
            t: "Charge mentale",
            d: "Cette petite voix qui ne s'arrête jamais.",
            img: IMAGES.tea,
            imgSrcSet: IMAGES.teaSrcSet,
            imgSizes: "64px",
        },
        {
            span: "col-span-12 md:col-span-6",
            variant: "image-wide",
            n: "05",
            t: "Changements de saison",
            d: "Accompagner ces bascules — automne, printemps, transitions.",
            img: IMAGES.natureWalk,
            imgSrcSet: IMAGES.natureWalkSrcSet,
            imgSizes: "(max-width: 768px) 100vw, 50vw",
        },
        {
            span: "col-span-12 md:col-span-6",
            variant: "editorial-wide",
            n: "06",
            t: "Envie de prendre soin de soi",
            d: "Parfois, il n'y a pas d'autre raison. C'est déjà bien assez.",
        },
    ];

    return (
        <section className="relative py-24 lg:py-32 border-b border-line/70 lg:border-t overflow-x-clip">
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10">
                {/* Intro — full width top */}
                <div className="grid grid-cols-12 gap-6 sm:gap-10 mb-12 sm:mb-14 lg:mb-20 items-end">
                    <div className="col-span-12 lg:col-span-7 min-w-0">
                        <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] sm:tracking-[0.3em] text-brown-soft mb-5 sm:mb-6">
                            Chapitre 01 · Pour qui
                        </div>
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.9 }}
                            className="font-serif font-light text-[clamp(2rem,8.5vw,3rem)] md:text-7xl leading-[1.05] sm:leading-[1.02] text-brown text-balance max-w-full"
                        >
                            On vient rarement <em className="italic text-terracotta-deep">par hasard.</em>
                        </motion.h2>
                    </div>
                    <div className="col-span-12 lg:col-span-5 min-w-0">
                        <p className="text-sm sm:text-base text-brown-soft leading-relaxed max-w-md lg:ml-auto">
                            La réflexologie n'est pas un remède ni un
                            diagnostic. C'est un espace pour ralentir,
                            écouter, et laisser le corps retrouver ses
                            propres appuis.
                        </p>
                    </div>
                </div>

                {/* Bento grid of situations */}
                <div className="grid grid-cols-12 auto-rows-[minmax(140px,auto)] gap-3 sm:gap-4 lg:gap-6">
                    {items.map((it, i) => (
                        <SituationCard key={it.n} item={it} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}

function SituationCard({ item, index }) {
    const common =
        "relative overflow-hidden rounded-md p-5 sm:p-6 lg:p-8 flex flex-col justify-between gap-4 group h-full min-w-0";

    return (
        <motion.article
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{
                duration: 0.9,
                delay: index * 0.06,
                ease: [0.22, 1, 0.36, 1],
            }}
            className={`min-w-0 ${item.span}`}
        >
            {item.variant === "image-dark" && (
                <div
                    className={`${common} bg-brown text-creme aspect-[16/11] sm:aspect-[16/10] md:aspect-auto md:min-h-[340px] lg:min-h-full`}
                >
                    <img
                        src={item.img}
                        srcSet={item.imgSrcSet}
                        sizes={item.imgSizes}
                        alt={item.t}
                        className="absolute inset-0 w-full h-full object-cover object-center opacity-60 transition-transform duration-[1400ms] group-hover:scale-105"
                        loading="lazy"
                        decoding="async"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-brown via-brown/50 to-brown/10" />
                    <div className="relative flex items-center gap-3 text-creme/80">
                        <span className="font-serif italic text-terracotta text-sm">
                            {item.n}
                        </span>
                        <span className="w-10 h-px bg-creme/40" />
                    </div>
                    <div className="relative">
                        <h3 className="font-serif font-light text-[clamp(1.75rem,6.5vw,2.5rem)] md:text-5xl lg:text-6xl leading-tight text-creme">
                            {item.t}
                        </h3>
                        <p className="mt-3 sm:mt-4 max-w-md text-creme/80 text-sm md:text-base leading-relaxed">
                            {item.d}
                        </p>
                    </div>
                </div>
            )}

            {item.variant === "tint-terracotta" && (
                <div className={`${common} bg-terracotta/18 text-brown min-h-[160px] sm:min-h-[180px]`}>
                    <span className="font-serif italic text-terracotta-deep text-sm">
                        {item.n}
                    </span>
                    <div>
                        <h3 className="font-serif font-light text-[clamp(1.5rem,5.5vw,2rem)] md:text-4xl leading-tight text-brown">
                            {item.t}
                        </h3>
                        <p className="mt-2 text-sm text-brown-soft leading-relaxed">
                            {item.d}
                        </p>
                    </div>
                </div>
            )}

            {item.variant === "dark" && (
                <div className={`${common} bg-forest text-creme min-h-[160px] sm:min-h-[180px]`}>
                    <span className="font-serif italic text-terracotta text-sm">
                        {item.n}
                    </span>
                    <div>
                        <h3 className="font-serif font-light text-[clamp(1.5rem,5.5vw,2rem)] md:text-4xl leading-tight text-creme">
                            {item.t}
                        </h3>
                        <p className="mt-2 text-sm text-creme/70 leading-relaxed">
                            {item.d}
                        </p>
                    </div>
                </div>
            )}

            {item.variant === "tint-sage-image" && (
                <div className={`${common} bg-sage/25 text-brown min-h-[200px] sm:min-h-[240px]`}>
                    <div className="flex items-center justify-between gap-3">
                        <span className="font-serif italic text-terracotta-deep text-sm">
                            {item.n}
                        </span>
                        <div className="w-14 h-14 sm:w-16 sm:h-16 shrink-0 rounded-full overflow-hidden border border-brown/20">
                            <img
                                src={item.img}
                                srcSet={item.imgSrcSet}
                                sizes={item.imgSizes}
                                alt={item.t}
                                className="w-full h-full object-cover"
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                    </div>
                    <div>
                        <h3 className="font-serif font-light text-[clamp(1.5rem,5.5vw,2rem)] md:text-4xl leading-tight text-brown">
                            {item.t}
                        </h3>
                        <p className="mt-2 text-sm text-brown-soft leading-relaxed max-w-sm">
                            {item.d}
                        </p>
                    </div>
                </div>
            )}

            {item.variant === "image-wide" && (
                <div
                    className={`${common} bg-brown/5 text-brown aspect-[16/11] sm:aspect-[16/10] md:aspect-auto md:min-h-[240px] lg:min-h-[280px]`}
                >
                    <img
                        src={item.img}
                        srcSet={item.imgSrcSet}
                        sizes={item.imgSizes}
                        alt={item.t}
                        className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-[1400ms] group-hover:scale-105"
                        loading="lazy"
                        decoding="async"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-brown/75 via-brown/35 to-transparent" />
                    <div className="relative flex items-center gap-3 text-creme/80">
                        <span className="font-serif italic text-terracotta text-sm">
                            {item.n}
                        </span>
                        <span className="w-10 h-px bg-creme/40" />
                    </div>
                    <div className="relative">
                        <h3 className="font-serif font-light text-[clamp(1.5rem,5.5vw,2rem)] md:text-4xl lg:text-5xl leading-tight text-creme">
                            {item.t}
                        </h3>
                        <p className="mt-2 sm:mt-3 max-w-md text-creme/85 text-sm md:text-base leading-relaxed">
                            {item.d}
                        </p>
                    </div>
                </div>
            )}

            {item.variant === "editorial-wide" && (
                <div className="relative overflow-hidden rounded-md p-5 sm:p-6 lg:p-8 bg-off-white border border-line min-h-[200px] sm:min-h-[240px] h-full flex flex-col justify-between gap-4 sm:gap-6 min-w-0">
                    <span className="font-serif italic text-terracotta-deep text-sm shrink-0">
                        {item.n}
                    </span>
                    <div>
                        <h3 className="font-serif font-light text-[clamp(1.5rem,5.5vw,2rem)] md:text-4xl italic text-brown leading-tight">
                            {item.t}
                        </h3>
                        <p className="mt-3 text-sm text-brown-soft leading-relaxed max-w-xs">
                            {item.d}
                        </p>
                    </div>
                </div>
            )}
        </motion.article>
    );
}

/* ---------------- Bento Prestations ---------------- */
function Bento() {
    return (
        <section className="relative py-24 lg:py-32">
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                    <div className="min-w-0">
                        <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] sm:tracking-[0.3em] text-brown-soft mb-4">
                            Chapitre 02 · Prestations
                        </div>
                        <h2 className="font-serif font-light text-[clamp(2rem,8vw,2.5rem)] md:text-6xl leading-[1.02] text-brown max-w-3xl">
                            Quatre pratiques,
                            <br />
                            <em className="italic text-forest">une seule attention.</em>
                        </h2>
                    </div>
                    <Link
                        to="/prestations"
                        className="group inline-flex items-center gap-2 text-sm text-brown border-b border-brown/40 pb-1 hover:border-terracotta hover:text-terracotta-deep shrink-0"
                    >
                        Voir toutes les prestations
                        <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                </div>

                {/* Irregular grid: 12 cols — stack until lg to avoid cramped split layouts */}
                <div className="grid grid-cols-12 gap-3 sm:gap-4 lg:gap-6">
                    {/* Plantaire — large */}
                    <BentoCard
                        p={PRESTATIONS[0]}
                        className="col-span-12 lg:col-span-7 lg:row-span-2 aspect-[16/11] sm:aspect-[16/10] lg:aspect-auto lg:min-h-[560px]"
                        variant="large"
                    />
                    {/* Palmaire — compact */}
                    <BentoCard
                        p={PRESTATIONS[1]}
                        className="col-span-12 lg:col-span-5 lg:min-h-[270px]"
                        variant="compact"
                    />
                    {/* Faciale — vertical tools */}
                    <BentoCard
                        p={PRESTATIONS[2]}
                        className="col-span-12 lg:col-span-5 lg:min-h-[270px]"
                        variant="tools"
                    />
                    {/* Chan'Beauté — editorial */}
                    <BentoCard
                        p={PRESTATIONS[3]}
                        className="col-span-12"
                        variant="editorial"
                    />
                </div>
            </div>
        </section>
    );
}

function BentoCard({ p, className = "", variant = "large" }) {
    const bg = {
        sage: "bg-sage/25",
        terracotta: "bg-terracotta/15",
        forest: "bg-forest/10",
        "terracotta-deep": "bg-terracotta-deep/10",
    }[p.color];

    return (
        <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            data-testid={TID.bento(p.slug)}
            className={`group relative overflow-hidden rounded-2xl ${bg} border border-line/60 min-w-0 ${className}`}
        >
            <Link
                to={`/prestations/${p.slug}`}
                className="block w-full h-full"
            >
                {variant === "large" && (
                    <div className="relative w-full h-full min-h-0 lg:min-h-[380px]">
                        <img
                            src={p.image}
                            srcSet={
                                {
                                    [IMAGES.foot]: IMAGES.footSrcSet,
                                    [IMAGES.dienChan]: IMAGES.dienChanSrcSet,
                                    [IMAGES.dienChanSoin]: IMAGES.dienChanSrcSet,
                                    [IMAGES.face]: IMAGES.faceSrcSet,
                                    [IMAGES.hand]: undefined,
                                }[p.image]
                            }
                            sizes="(max-width: 1024px) 100vw, 50vw"
                            alt={p.name}
                            className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                            loading="lazy"
                            decoding="async"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-brown/70 via-brown/10 to-transparent" />
                        <div className="absolute top-5 left-5 sm:top-6 sm:left-6 flex items-center gap-3 text-creme/90">
                            <span className="font-serif italic text-lg">
                                {p.num}
                            </span>
                            <span className="w-8 h-px bg-creme/70" />
                            <span className="text-[10px] uppercase tracking-[0.28em]">
                                Signature
                            </span>
                        </div>
                        <div className="absolute bottom-5 left-5 right-5 sm:bottom-6 sm:left-6 sm:right-6 text-creme">
                            <h3 className="font-serif text-[clamp(1.75rem,6vw,2.5rem)] md:text-5xl font-light leading-tight">
                                {p.name}
                            </h3>
                            <p className="mt-2 sm:mt-3 max-w-md text-sm sm:text-base text-creme/85">
                                {p.short}
                            </p>
                            <div className="mt-4 sm:mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                                <span>{p.duration}</span>
                                <span className="text-creme/80">·</span>
                                <span>{p.price}</span>
                                <span className="sm:ml-auto inline-flex items-center gap-1 border-b border-creme/60 pb-0.5">
                                    Découvrir
                                    <ArrowUpRight className="w-3.5 h-3.5" />
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {variant === "compact" && (
                    <div className="relative h-full p-6 sm:p-8 flex flex-col justify-between min-h-[240px] sm:min-h-[270px]">
                        <div className="flex items-start justify-between gap-3">
                            <span className="font-serif italic text-sm text-brown-soft">
                                {p.num}
                            </span>
                            <span className="w-10 h-10 shrink-0 rounded-full border border-brown/30 grid place-items-center text-brown transition-transform duration-500 group-hover:rotate-45">
                                <ArrowUpRight className="w-4 h-4" />
                            </span>
                        </div>
                        <div>
                            <h3 className="font-serif text-[clamp(1.5rem,5vw,1.875rem)] md:text-3xl font-light leading-tight text-brown">
                                {p.name}
                            </h3>
                            <p className="mt-3 text-sm text-brown-soft leading-relaxed">
                                {p.short}
                            </p>
                            <div className="mt-6 flex items-center gap-3 text-xs text-brown-soft">
                                <span>{p.duration}</span>
                                <span>·</span>
                                <span>{p.price}</span>
                            </div>
                        </div>
                    </div>
                )}

                {variant === "tools" && (
                    <div className="relative h-full min-h-[240px] sm:min-h-[270px] grid grid-cols-1 min-[420px]:grid-cols-[1.35fr_1fr]">
                        <div className="p-6 sm:p-8 flex flex-col justify-between order-2 min-[420px]:order-1 min-w-0">
                            <span className="font-serif italic text-sm text-brown-soft">
                                {p.num}
                            </span>
                            <div>
                                <h3 className="font-serif text-[clamp(1.5rem,5vw,1.875rem)] md:text-3xl font-light leading-tight text-brown">
                                    {p.name}
                                </h3>
                                <div className="mt-6 flex items-center gap-3 text-xs text-brown-soft">
                                    <span>{p.duration}</span>
                                    <span>·</span>
                                    <span>{p.price}</span>
                                </div>
                            </div>
                        </div>
                        <div className="relative overflow-hidden order-1 min-[420px]:order-2 aspect-[16/10] min-[420px]:aspect-auto min-[420px]:h-full min-[420px]:min-h-[240px] bg-creme">
                            <img
                                src={IMAGES.dienChan}
                                srcSet={IMAGES.dienChanSrcSet}
                                sizes="(max-width: 420px) 100vw, 40vw"
                                alt={p.name}
                                className="absolute inset-0 w-full h-full object-contain object-center p-3 sm:p-4 transition-transform duration-[1400ms] group-hover:scale-105"
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                    </div>
                )}

                {variant === "editorial" && (
                    <div className="relative h-full grid grid-cols-1 lg:grid-cols-12">
                        <div className="lg:col-span-5 relative overflow-hidden aspect-[2/1] sm:aspect-[21/9] lg:aspect-auto lg:min-h-[320px]">
                            <img
                                src={IMAGES.face}
                                srcSet={IMAGES.faceSrcSet}
                                sizes="(max-width: 1024px) 100vw, 40vw"
                                alt="Soin du visage Chan’Beauté"
                                className="absolute inset-0 w-full h-full object-cover object-[center_30%] transition-transform duration-[1400ms] group-hover:scale-105"
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                        <div className="lg:col-span-7 p-6 sm:p-8 lg:p-12 flex flex-col justify-center min-w-0">
                            <div className="flex items-center gap-3 text-brown-soft mb-4 sm:mb-5">
                                <span className="font-serif italic text-sm">
                                    {p.num}
                                </span>
                                <span className="w-8 h-px bg-brown/30" />
                                <span className="text-[10px] uppercase tracking-widest">
                                    Éditorial
                                </span>
                            </div>
                            <h3 className="font-serif text-[clamp(1.75rem,6vw,2.5rem)] md:text-5xl font-light leading-tight text-brown">
                                {p.name}
                            </h3>
                            <p className="mt-3 sm:mt-4 text-sm sm:text-base text-brown-soft max-w-lg leading-relaxed">
                                {p.short} Un soin naturel du visage, sensoriel
                                et raffiné, entre bien-être profond et éclat.
                            </p>
                            <div className="mt-6 sm:mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-brown">
                                <span>{p.duration}</span>
                                <span className="text-brown-soft/50">·</span>
                                <span>{p.price}</span>
                                <span className="sm:ml-auto inline-flex items-center gap-1 border-b border-brown pb-0.5">
                                    Découvrir la pratique
                                    <ArrowUpRight className="w-3.5 h-3.5" />
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </Link>
        </motion.div>
    );
}

/* ---------------- Joëlle intro ---------------- */
function JoelleSection() {
    return (
        <section className="relative py-16 min-[385px]:py-20 sm:py-24 lg:py-32 bg-forest/[0.06] overflow-x-clip">
            <div className="max-w-[1440px] mx-auto px-3 min-[385px]:px-4 sm:px-6 lg:px-10 grid grid-cols-12 gap-6 min-[385px]:gap-8 sm:gap-10 items-center">
                <div className="col-span-12 lg:col-span-5 relative order-2 lg:order-1 mb-8 min-[385px]:mb-10 lg:mb-0 min-w-0">
                    <motion.div
                        initial={{ opacity: 0, scale: 1.05 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.2 }}
                        className="relative aspect-[16/10] min-[385px]:aspect-[16/11] sm:aspect-[16/10] lg:aspect-[4/5] overflow-hidden rounded-md"
                    >
                        <img
                            src={IMAGES.hands_massage}
                            srcSet={IMAGES.handsMassageSrcSet}
                            sizes="(max-width: 1024px) 100vw, 40vw"
                            alt="Gestes de soin et de réflexologie"
                            className="w-full h-full object-cover object-center"
                            loading="lazy"
                            decoding="async"
                        />
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9, delay: 0.3 }}
                        className="absolute right-1 min-[385px]:right-1.5 sm:right-3 lg:-right-4 -bottom-4 min-[385px]:-bottom-5 sm:-bottom-6 bg-creme border border-line px-2.5 py-2 min-[385px]:px-3 min-[385px]:py-2.5 sm:px-6 sm:py-4 rounded shadow-sm max-w-[min(140px,42%)] min-[385px]:max-w-[min(160px,46%)] sm:max-w-[220px]"
                    >
                        <Quote className="w-3 h-3 min-[385px]:w-3.5 min-[385px]:h-3.5 sm:w-4 sm:h-4 text-terracotta mb-1 min-[385px]:mb-1.5 sm:mb-2" />
                        <p className="font-hand text-sm min-[385px]:text-base sm:text-2xl text-brown leading-tight">
                            Je vous accueille comme j'aime être accueillie.
                        </p>
                        <p className="mt-1 min-[385px]:mt-1.5 sm:mt-2 text-[10px] sm:text-xs text-brown-soft">— Joëlle</p>
                    </motion.div>
                </div>

                <div className="col-span-12 lg:col-span-7 lg:pl-12 order-1 lg:order-2 min-w-0">
                    <div className="text-[9px] min-[385px]:text-[10px] sm:text-xs uppercase tracking-[0.12em] min-[385px]:tracking-[0.18em] sm:tracking-[0.3em] text-brown-soft mb-4 min-[385px]:mb-5 sm:mb-6">
                        Chapitre 03 · La praticienne
                    </div>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9 }}
                        className="font-serif font-light text-[clamp(1.55rem,7.5vw,2.25rem)] md:text-6xl leading-[1.08] text-brown max-w-full break-words hyphens-auto"
                    >
                        Une pratique <em className="italic text-terracotta-deep">à hauteur d'humain.</em>
                    </motion.h2>
                    <div className="mt-5 min-[385px]:mt-6 sm:mt-8 space-y-3.5 min-[385px]:space-y-4 sm:space-y-5 text-[13px] min-[385px]:text-sm sm:text-base text-brown-soft leading-relaxed max-w-xl">
                        <p>
                            Ma rencontre avec la réflexologie remonte à 2018.
                            Ce que j'y ai découvert — la précision du geste,
                            la puissance de l'écoute, le respect du rythme —
                            m'a fait choisir ce métier sans hésiter.
                        </p>
                        <p>
                            Formée par la FFPER selon la méthode Eunice Ingham,
                            puis au Dien Chan et à Chan'Beauté, je propose
                            aujourd'hui une approche globale, douce et
                            profondément personnalisée.
                        </p>
                    </div>
                    <div className="mt-7 min-[385px]:mt-8 sm:mt-10 grid grid-cols-1 min-[385px]:grid-cols-2 gap-x-3 gap-y-4 min-[385px]:gap-x-4 min-[385px]:gap-y-5 sm:gap-6 max-w-md">
                        {[
                            { k: "FFPER", v: "Méthode Eunice Ingham" },
                            { k: "Dien Chan", v: "Réflexologie faciale" },
                            { k: "Chan'Beauté", v: "Soin naturel du visage" },
                            { k: "Approche", v: "Globale & personnalisée" },
                        ].map((it) => (
                            <div key={it.k} className="border-t border-brown/20 pt-2.5 min-[385px]:pt-3 min-w-0">
                                <div className="font-serif text-[15px] min-[385px]:text-base sm:text-lg text-brown leading-snug">
                                    {it.k}
                                </div>
                                <div className="text-[11px] sm:text-xs text-brown-soft mt-0.5 leading-snug">
                                    {it.v}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-7 min-[385px]:mt-8 sm:mt-10">
                        <Link
                            to="/a-propos"
                            className="inline-flex items-center gap-2 text-sm text-brown border-b border-brown/40 pb-1 hover:border-terracotta hover:text-terracotta-deep"
                        >
                            Lire mon parcours complet
                            <ArrowUpRight className="w-4 h-4 shrink-0" />
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ---------------- Timeline ---------------- */
function Timeline() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start 80%", "end 30%"],
    });
    const scaleY = useSpring(scrollYProgress, { stiffness: 60, damping: 20 });

    return (
        <section ref={ref} className="relative py-24 lg:py-32">
            <div className="max-w-[1200px] mx-auto px-4 max-[350px]:px-3 sm:px-6 lg:px-10">
                <div className="max-w-2xl mb-16">
                    <div className="text-xs uppercase tracking-[0.3em] text-brown-soft mb-4">
                        Chapitre 04 · Parcours
                    </div>
                    <h2 className="font-serif font-light text-4xl md:text-6xl leading-[1.02] text-brown">
                        Un chemin patient,<br />
                        <em className="italic">une formation continue.</em>
                    </h2>
                </div>

                <div className="relative">
                    {/* Line — same left as dots, centered via -translate-x-1/2 */}
                    <div className="absolute left-3 md:left-10 top-0 bottom-0 w-px -translate-x-1/2 bg-line" />
                    <motion.div
                        style={{ scaleY }}
                        className="timeline-line absolute left-3 md:left-10 top-0 bottom-0 w-px -translate-x-1/2 origin-top"
                    />

                    <ul className="space-y-14">
                        {TIMELINE.map((t, i) => (
                            <TimelineItem key={t.year} t={t} index={i} />
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    );
}

function TimelineItem({ t, index }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-120px" });
    return (
        <li ref={ref} className="relative pl-10 max-[350px]:pl-8 md:pl-24">
            <span
                className={`absolute left-3 md:left-10 top-2 w-3 h-3 -translate-x-1/2 rounded-full border-2 border-brown transition-all duration-700 ${
                    inView ? "bg-terracotta scale-100" : "bg-creme scale-90"
                }`}
            />
            <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.9, delay: 0.1 }}
                className="grid grid-cols-12 gap-4 max-[350px]:gap-3 md:gap-6 items-baseline min-w-0"
            >
                <div className="col-span-12 md:col-span-3 min-w-0">
                    <div className="font-serif italic text-xl max-[350px]:text-lg text-terracotta-deep">
                        {t.year}
                    </div>
                </div>
                <div className="col-span-12 md:col-span-9 min-w-0">
                    <h3 className="font-serif text-2xl max-[350px]:text-xl md:text-3xl text-brown font-light break-words">
                        {t.title}
                    </h3>
                    <p className="mt-2 text-sm max-[350px]:text-xs text-brown-soft leading-relaxed max-w-2xl break-words">
                        {t.text}
                    </p>
                </div>
            </motion.div>
        </li>
    );
}

/* ---------------- Séance ---------------- */
function Seance() {
    return (
        <section className="relative py-24 lg:py-32 bg-off-white border-y border-line/70">
            <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-brown-soft mb-4">
                            Chapitre 05 · Le déroulement
                        </div>
                        <h2 className="font-serif font-light text-4xl md:text-6xl leading-[1.02] text-brown">
                            Ce que <em className="italic text-forest">l'on vit</em>,<br />
                            en quatre temps.
                        </h2>
                    </div>
                    <p className="max-w-sm text-brown-soft leading-relaxed">
                        Chaque séance est unique. Voici les repères que
                        vous retrouverez, quelle que soit la pratique.
                    </p>
                </div>

                <div className="space-y-2">
                    {SESSION_STEPS.map((s, i) => (
                        <StepRow key={s.num} s={s} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}

function StepRow({ s, index }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="group grid grid-cols-12 gap-6 py-10 border-t border-line hover:bg-creme/80 transition-colors px-4 -mx-4 rounded-lg"
        >
            <div className="col-span-3 md:col-span-2">
                <div className="font-serif italic text-4xl md:text-6xl text-terracotta-deep">
                    {s.num}
                </div>
            </div>
            <div className="col-span-9 md:col-span-6">
                <h3 className="font-serif text-2xl md:text-3xl text-brown font-light leading-tight">
                    {s.title}
                </h3>
            </div>
            <div className="col-span-12 md:col-span-4">
                <p className="text-brown-soft leading-relaxed">{s.text}</p>
            </div>
        </motion.div>
    );
}

/* ---------------- Marquee band ---------------- */
function MarqueeBand() {
    return (
        <section className="py-10 border-y border-line/60 bg-creme">
            <Marquee
                speed={45}
                items={[
                    "Réflexologie plantaire",
                    "·",
                    "Réflexologie palmaire",
                    "·",
                    "Dien Chan",
                    "·",
                    "Chan'Beauté",
                    "·",
                    "Cabinet à Challes-les-Eaux",
                    "·",
                    "Stress & tensions",
                    "·",
                    "Fatigue",
                    "·",
                    "Sommeil",
                    "·",
                    "Charge mentale",
                    "·",
                    "Lâcher-prise",
                    "·",
                ]}
                itemClassName="font-serif italic text-4xl md:text-6xl text-brown-soft"
            />
        </section>
    );
}

/* ---------------- Réassurance / Avis ---------------- */
function Reassurance() {
    const items = REVIEWS.slice(0, 3);
    return (
        <section className="py-24 lg:py-32">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14 lg:mb-16">
                    <div className="max-w-3xl">
                        <div className="text-xs uppercase tracking-[0.3em] text-brown-soft mb-4">
                            Chapitre 07 · Confiance
                        </div>
                        <h2 className="font-serif font-light text-4xl md:text-6xl leading-[1.05] text-brown">
                            Les personnes que j'accompagne{" "}
                            <em className="italic text-terracotta-deep">reviennent.</em>
                        </h2>
                        <p className="mt-6 text-brown-soft leading-relaxed max-w-xl">
                            Des avis Google authentiques — douceur, écoute et
                            résultats ressentis dès les premières séances.
                        </p>
                    </div>
                    <GoogleRatingBadge className="w-fit self-start md:self-auto" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
                    {items.map((t, i) => (
                        <motion.div
                            key={t.author}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: i * 0.08 }}
                            className="border border-line rounded-md p-6 bg-off-white flex flex-col h-full"
                        >
                            <Quote className="w-4 h-4 text-terracotta mb-3" />
                            <p className="font-serif text-xl italic text-brown leading-snug flex-1">
                                « {t.quote} »
                            </p>
                            <p className="mt-6 text-xs uppercase tracking-widest text-brown-soft">
                                {t.author}
                            </p>
                        </motion.div>
                    ))}
                </div>

                {GOOGLE_RATING.url && (
                    <div className="mt-10">
                        <a
                            href={GOOGLE_RATING.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm text-brown border-b border-brown/40 pb-1 hover:border-terracotta"
                        >
                            Voir tous les avis sur Google →
                        </a>
                    </div>
                )}
            </div>
        </section>
    );
}

/* ---------------- Cabinet ---------------- */
function Cabinet() {
    return (
        <section className="relative py-24 lg:py-32 bg-brown text-creme overflow-hidden">
            <div className="absolute inset-0 opacity-10 pointer-events-none" aria-hidden="true">
                <img
                    src={IMAGES.alps}
                    alt=""
                    className="w-full h-full object-cover"
                    width={900}
                    height={600}
                    loading="lazy"
                    decoding="async"
                    fetchPriority="low"
                />
            </div>
            <div className="relative max-w-[1440px] mx-auto px-4 max-[490px]:px-3 sm:px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 max-[490px]:gap-6 lg:gap-10 items-center">
                <div className="lg:col-span-6 min-w-0 max-w-full">
                    <div className="text-xs uppercase tracking-[0.3em] text-creme/80 mb-6">
                        Chapitre 06 · Le cabinet
                    </div>
                    <h2 className="font-serif font-light text-[clamp(1.75rem,7.5vw,2.25rem)] md:text-6xl leading-[1.02] text-creme break-words">
                        Un lieu simple,<br />
                        <em className="italic text-terracotta">baigné de lumière.</em>
                    </h2>
                    <p className="mt-6 max-[490px]:mt-5 sm:mt-8 text-sm max-[490px]:text-xs sm:text-base text-creme/70 leading-relaxed max-w-md break-words">
                        Le cabinet est situé à Challes-les-Eaux, à quelques
                        minutes de Chambéry, dans un cadre calme et
                        accessible facilement.
                    </p>

                    <div className="mt-8 max-[490px]:mt-6 sm:mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5 max-[490px]:gap-4 sm:gap-6">
                        <div className="flex gap-3 min-w-0">
                            <MapPin className="w-5 h-5 text-terracotta shrink-0 mt-1" />
                            <div className="min-w-0">
                                <div className="text-xs uppercase tracking-widest text-creme/80">
                                    Adresse
                                </div>
                                <div className="text-sm max-[490px]:text-xs text-creme mt-1 leading-relaxed break-words">
                                    {CONTACT.address.street}
                                    <br />
                                    {CONTACT.address.zip} {CONTACT.address.city}
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-3 min-w-0">
                            <Phone className="w-5 h-5 text-terracotta shrink-0 mt-1" />
                            <div className="min-w-0">
                                <div className="text-xs uppercase tracking-widest text-creme/80">
                                    Téléphone
                                </div>
                                <a
                                    href={`tel:${CONTACT.phoneRaw}`}
                                    className="text-sm max-[490px]:text-xs text-creme mt-1 block hover:text-terracotta"
                                >
                                    {CONTACT.phone}
                                </a>
                            </div>
                        </div>
                        <div className="flex gap-3 min-w-0">
                            <Clock className="w-5 h-5 text-terracotta shrink-0 mt-1" />
                            <div className="min-w-0">
                                <div className="text-xs uppercase tracking-widest text-creme/80">
                                    Horaires
                                </div>
                                <div className="text-sm max-[490px]:text-xs text-creme mt-1 break-words">
                                    Lun. 14h–18h · Mar./Jeu. 9h–12h & 13h30–18h
                                    <br />
                                    Mer. 9h–12h · Ven. 9h–18h · Sam. sur RDV
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 max-[490px]:mt-6 sm:mt-10">
                        <Link
                            to="/contact"
                            className="inline-flex items-center gap-2 text-sm max-[490px]:text-xs bg-terracotta text-creme rounded-full px-5 max-[490px]:px-4 py-3 hover:bg-terracotta-deep"
                        >
                            Prendre rendez-vous
                            <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>

                <div className="lg:col-span-6 min-w-0 max-w-full">
                    <motion.div
                        initial={{ opacity: 0, scale: 1.03 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.2 }}
                        className="relative w-full max-w-full"
                    >
                        <div className="relative w-full h-[280px] sm:h-[320px] md:h-[360px] lg:h-auto lg:aspect-[4/5] rounded-md overflow-hidden">
                            <img
                                src={IMAGES.room}
                                alt="Espace de soin calme et lumineux"
                                className="w-full h-full max-w-full object-cover"
                            />
                        </div>
                        <div className="absolute -bottom-6 -left-6 bg-creme text-brown rounded-md overflow-hidden shadow-xl w-40 sm:w-48 lg:w-56 aspect-square hidden md:block">
                            <iframe
                                title="Carte du cabinet — 1981 Route Royale, Challes-les-Eaux"
                                src={CONTACT.mapEmbedUrl}
                                className="w-full h-full grayscale-[.5]"
                                loading="lazy"
                            />
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

/* ---------------- FAQ ---------------- */
function FAQShort() {
    return (
        <section className="py-24 lg:py-32">
            <div className="max-w-[1000px] mx-auto px-6 lg:px-10">
                <div className="mb-14">
                    <div className="text-xs uppercase tracking-[0.3em] text-brown-soft mb-4">
                        Chapitre 08 · Questions
                    </div>
                    <h2 className="font-serif font-light text-4xl md:text-6xl leading-[1.02] text-brown">
                        Les questions que <em className="italic">l'on me pose souvent.</em>
                    </h2>
                </div>
                <Accordion type="single" collapsible className="w-full">
                    {FAQ_HOME.map((f, i) => (
                        <AccordionItem
                            key={i}
                            value={`item-${i}`}
                            className="border-b border-line py-1"
                        >
                            <AccordionTrigger className="text-left font-serif text-xl md:text-2xl text-brown hover:no-underline py-5">
                                {f.q}
                            </AccordionTrigger>
                            <AccordionContent className="text-brown-soft leading-relaxed max-w-3xl">
                                {f.a}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
                <div className="mt-10 flex flex-wrap gap-4">
                    <Link
                        to="/faq"
                        className="inline-flex items-center gap-2 text-sm text-brown border-b border-brown/40 pb-1 hover:border-terracotta"
                    >
                        Voir toutes les questions
                        <ArrowUpRight className="w-4 h-4" />
                    </Link>
                    <Link
                        to="/premiere-seance"
                        className="inline-flex items-center gap-2 text-sm text-brown-soft hover:text-terracotta-deep"
                    >
                        Première séance →
                    </Link>
                    <Link
                        to="/guides"
                        className="inline-flex items-center gap-2 text-sm text-brown-soft hover:text-terracotta-deep"
                    >
                        Guides →
                    </Link>
                </div>
            </div>
        </section>
    );
}

/* ---------------- Final CTA ---------------- */
function FinalCTA() {
    return (
        <section className="relative py-[clamp(3.5rem,6vw,8rem)] bg-creme-deep">
            <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-y-8 gap-x-[clamp(0.75rem,2.2vw,2.5rem)] items-center">
                <div className="md:col-span-5 min-w-0">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.1 }}
                        className="relative mx-auto md:mx-0 w-full max-w-[220px] sm:max-w-[260px] md:max-w-none aspect-[4/5] rounded-t-[999px] overflow-hidden"
                    >
                        <img
                            src={IMAGES.portrait}
                            alt="Portrait de Joëlle Berthaud, réflexologue"
                            className="w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                            width={600}
                            height={750}
                        />
                    </motion.div>
                </div>
                <div className="md:col-span-7 min-w-0 text-center md:text-left md:pl-[clamp(0rem,1.5vw,2.5rem)]">
                    <p className="font-hand text-[clamp(1.35rem,2.8vw,1.875rem)] text-terracotta-deep mb-[clamp(0.75rem,1.5vw,1.5rem)]">
                        Un mot personnel,
                    </p>
                    <h2 className="font-serif font-light text-[clamp(1.65rem,3.4vw+0.4rem,3.75rem)] leading-[1.05] text-brown break-words">
                        Vous ne savez pas <em className="italic">quelle séance choisir ?</em>
                    </h2>
                    <p className="mt-[clamp(0.875rem,1.8vw,1.5rem)] text-[clamp(0.8125rem,0.7rem+0.45vw,1rem)] text-brown-soft leading-relaxed max-w-lg mx-auto md:mx-0">
                        Échangeons quelques minutes ensemble pour identifier
                        ce qui vous conviendrait le mieux. C'est gratuit et
                        sans engagement.
                    </p>
                    <div className="mt-[clamp(1.25rem,2.5vw,2.5rem)] flex flex-wrap justify-center md:justify-start gap-3">
                        <a
                            href={`tel:${CONTACT.phoneRaw}`}
                            data-testid="home-cta-call"
                            className="inline-flex items-center gap-2 bg-brown text-creme rounded-full px-5 sm:px-6 py-2.5 sm:py-3 text-sm hover:bg-terracotta-deep"
                        >
                            <Phone className="w-4 h-4" />
                            Appeler Joëlle
                        </a>
                        <Link
                            to="/contact"
                            data-testid="home-cta-message"
                            className="inline-flex items-center gap-2 border border-brown text-brown rounded-full px-5 sm:px-6 py-2.5 sm:py-3 text-sm hover:bg-brown hover:text-creme"
                        >
                            Envoyer un message
                        </Link>
                        <Link
                            to="/premiere-seance"
                            className="inline-flex items-center gap-2 text-sm text-brown underline underline-offset-4 decoration-brown/30 hover:decoration-terracotta"
                        >
                            Première séance →
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* =============================================== */
export default function Home() {
    const seo = getPageSeo("/");
    return (
        <>
            <Seo {...seo} />
            <Hero />
            <Projections />
            <Bento />
            <JoelleSection />
            <Timeline />
            <Seance />
            <MarqueeBand />
            <Cabinet />
            <Reassurance />
            <FAQShort />
            <FinalCTA />
        </>
    );
}
